/* =========================================================
   <highlights-reel>
   Per-card playback: video plays once when card enters viewport,
   then freezes on its end-frame image. Replays each time the
   card re-enters the viewport. User can scroll/swipe freely;
   prev/next buttons jump one card at a time.
   ========================================================= */
(function () {
	'use strict';

	if (customElements.get('highlights-reel')) return;

	var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

	class HighlightsReel extends HTMLElement {
		connectedCallback() {
			this.track = this.querySelector('.highlights-reel__track');
			this.cards = Array.from(this.querySelectorAll('.highlights-reel__card'));
			this.dots = Array.from(this.querySelectorAll('.highlights-reel__dot'));
			this.prevBtn = this.querySelector('.highlights-reel__nav--prev');
			this.nextBtn = this.querySelector('.highlights-reel__nav--next');

			if (!this.track || this.cards.length === 0) return;

			this.cards.forEach(function (card) {
				var mediaBlock = card.querySelector('.highlights-reel__media-block');
				if (mediaBlock) mediaBlock.setAttribute('data-state', 'initial');
			});

			this._setupVideoEndedHandlers();
			this._setupVisibilityObserver();
			this._setupScrollSync();
			this._setupControls();

			// Show nav controls now that JS is alive (only on desktop, CSS handles hide)
			if (this.prevBtn) this.prevBtn.hidden = false;
			if (this.nextBtn) this.nextBtn.hidden = false;
			this._updateNavState();
		}

		_setupVideoEndedHandlers() {
			this.cards.forEach(function (card) {
				var video = card.querySelector('video');
				var mediaBlock = card.querySelector('.highlights-reel__media-block');
				if (!video || !mediaBlock) return;

				video.muted = true;
				video.playsInline = true;
				video.loop = false;
				video.controls = false;

				video.addEventListener('ended', function () {
					mediaBlock.setAttribute('data-state', 'ended');
				});

				video.addEventListener('play', function () {
					mediaBlock.setAttribute('data-state', 'playing');
				});

				video.addEventListener('error', function () {
					// Fall back to end-frame if the video fails
					mediaBlock.setAttribute('data-state', 'ended');
				});
			});
		}

		_setupVisibilityObserver() {
			var self = this;

			if (!('IntersectionObserver' in window)) {
				// Fallback: show everything in its revealed/ended state immediately
				this.cards.forEach(function (card) {
					var mediaBlock = card.querySelector('.highlights-reel__media-block');
					if (mediaBlock) mediaBlock.setAttribute('data-state', 'ended');
					card.classList.add('is-revealed');
				});
				return;
			}

			if (prefersReducedMotion) {
				// No video playback, but still reveal caption (CSS handles reduced-motion fade)
				this.cards.forEach(function (card) {
					var mediaBlock = card.querySelector('.highlights-reel__media-block');
					if (mediaBlock) mediaBlock.setAttribute('data-state', 'ended');
					card.classList.add('is-revealed');
				});
				return;
			}

			this._observer = new IntersectionObserver(function (entries) {
				entries.forEach(function (entry) {
					var card = entry.target;
					var video = card.querySelector('video');
					var mediaBlock = card.querySelector('.highlights-reel__media-block');

					if (entry.intersectionRatio >= 0.5) {
						// Card is the active/visible one → reveal + play
						card.classList.add('is-revealed');
						if (video && mediaBlock) {
							self._playCard(card, video, mediaBlock);
						}
					} else {
						// Card is below half-visible (peek state or fully off) → hide content + reset
						card.classList.remove('is-revealed');
						if (video && mediaBlock) {
							self._resetCard(card, video, mediaBlock);
						}
					}
				});
			}, {
				root: this.track,
				threshold: [0, 0.25, 0.5, 0.75, 1]
			});

			this.cards.forEach(function (card) {
				self._observer.observe(card);
			});
		}

		_playCard(card, video, mediaBlock) {
			// If already played, don't restart unless reset
			var state = mediaBlock.getAttribute('data-state');
			if (state === 'playing' || state === 'ended') return;

			var attemptPlay = function () {
				// Only seek if metadata is loaded (avoid throw on unloaded video)
				if (video.readyState >= 1) {
					try { video.currentTime = 0; } catch (e) { /* ignore */ }
				}
				try {
					var playPromise = video.play();
					if (playPromise && typeof playPromise.catch === 'function') {
						playPromise.catch(function () {
							// Autoplay blocked or other failure — freeze on end-frame
							mediaBlock.setAttribute('data-state', 'ended');
						});
					}
				} catch (e) {
					mediaBlock.setAttribute('data-state', 'ended');
				}
			};

			// readyState ≥ 2 (HAVE_CURRENT_DATA) means we can play immediately.
			// Otherwise wait for the video to load enough data — this is the
			// fix for the FIRST card not playing on initial scroll-into-view:
			// previously play() was called while the video was still loading
			// (preload="metadata" / "none"), the end-frame stayed visible until
			// data arrived, and to the user it looked like the video never played.
			if (video.readyState >= 2) {
				attemptPlay();
			} else {
				var onReady = function () {
					video.removeEventListener('canplay', onReady);
					// Only proceed if card is still in its initial state —
					// the user may have scrolled past while we were loading.
					if (mediaBlock.getAttribute('data-state') === 'initial') {
						attemptPlay();
					}
				};
				video.addEventListener('canplay', onReady, { once: true });
				// Nudge the browser to start loading if it hasn't already.
				try { video.load(); } catch (e) { /* ignore */ }
			}
		}

		_resetCard(card, video, mediaBlock) {
			// Pause immediately so the video stops accumulating time —
			// but DON'T snap currentTime back to 0 yet. With the
			// end-frame fading in over 700ms (see CSS), the user would
			// otherwise see the video visibly jump to its first frame
			// underneath the still-translucent end-frame.
			try { video.pause(); } catch (e) { /* ignore */ }
			mediaBlock.setAttribute('data-state', 'initial');

			// Delay the position reset until the end-frame is fully
			// opaque. Match the CSS transition duration (700ms).
			setTimeout(function () {
				// Only reset if we're still in the 'initial' state.
				// If the user scrolled back in within the fade window
				// and _playCard already started fresh playback,
				// skip — otherwise we'd interrupt the new play.
				if (mediaBlock.getAttribute('data-state') === 'initial') {
					try { video.currentTime = 0; } catch (e) { /* ignore */ }
				}
			}, 700);
		}

		_setupScrollSync() {
			var self = this;
			var raf = null;

			this.track.addEventListener('scroll', function () {
				if (raf) return;
				raf = requestAnimationFrame(function () {
					raf = null;
					self._updateCurrentDot();
					self._updateNavState();
				});
			}, { passive: true });
		}

		_updateCurrentDot() {
			if (this.dots.length === 0) return;
			var trackRect = this.track.getBoundingClientRect();
			var centerX = trackRect.left + trackRect.width / 2;
			var closestIdx = 0;
			var closestDist = Infinity;

			this.cards.forEach(function (card, i) {
				var rect = card.getBoundingClientRect();
				var cardCenter = rect.left + rect.width / 2;
				var dist = Math.abs(cardCenter - centerX);
				if (dist < closestDist) {
					closestDist = dist;
					closestIdx = i;
				}
			});

			this.dots.forEach(function (dot, i) {
				var isCurrent = i === closestIdx;
				dot.classList.toggle('is-current', isCurrent);
				dot.setAttribute('aria-selected', isCurrent ? 'true' : 'false');
			});
		}

		_updateNavState() {
			if (!this.prevBtn || !this.nextBtn) return;
			var maxScroll = this.track.scrollWidth - this.track.clientWidth;
			this.prevBtn.disabled = this.track.scrollLeft <= 2;
			this.nextBtn.disabled = this.track.scrollLeft >= maxScroll - 2;
		}

		_setupControls() {
			var self = this;

			if (this.prevBtn) {
				this.prevBtn.addEventListener('click', function () { self._scrollByCard(-1); });
			}
			if (this.nextBtn) {
				this.nextBtn.addEventListener('click', function () { self._scrollByCard(1); });
			}

			this.dots.forEach(function (dot) {
				dot.addEventListener('click', function () {
					var idx = parseInt(dot.getAttribute('data-index'), 10);
					if (!isNaN(idx) && self.cards[idx]) {
						self.cards[idx].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
					}
				});
			});
		}

		_scrollByCard(direction) {
			if (this.cards.length === 0) return;
			var firstCard = this.cards[0];
			var gap = parseInt(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap, 10) || 16;
			var step = firstCard.offsetWidth + gap;
			this.track.scrollBy({ left: step * direction, behavior: 'smooth' });
		}

		disconnectedCallback() {
			if (this._observer) this._observer.disconnect();
		}
	}

	customElements.define('highlights-reel', HighlightsReel);
})();
